import type { Underlying } from 'apiConnector/secondary-market/undelyingsApiConnector';
import type {
  ColumnGroupPreferenceState,
  ColumnPreferenceState,
  EditableFieldInfo,
  IBlotterPreferenceState,
} from 'components/shared/Blotter/Grid/blotter.model';
import type { IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';

import type { ProductCodeType, SecondaryMarketProduct } from '.';

type SecondaryMarketProductRefreshActions =
  | SecondaryMarketProductRefreshRequested
  | SecondaryMarketProductRefreshReceived
  | SecondaryMarketProductRefreshFailed;

type SecondaryMarketProductValueChangeActions =
  | SecondaryMarketProductsValueChanged
  | SecondaryMarketProductsValueChangedRequest
  | SecondaryMarketProductsSalesMessageChangedRequest
  | SecondaryMarketProductsCustodianOrClientChangedRequest
  | SecondaryMarketProductsValueChangedFailed
  | SecondaryMarketProductsValueChangedDone
  | SecondaryMarketProductsValueChangedSetEditableInfo;

type SecondaryMarketProductsColumnsActions =
  | SecondaryMarketProductsColumnsFetching
  | SecondaryMarketProductsColumnsFetched
  | SecondaryMarketProductsColumnsFetchFailed;

type SecondaryMarketProductsPreferenceStateActions =
  | SecondaryMarketProductsPreferenceStatePersisted
  | SecondaryMarketProductsPreferenceStatePersistenceFailed
  | SecondaryMarketProductsPreferenceStateLoaded
  | SecondaryMarketProductsPreferenceStateLoadingFailed
  | SecondaryMarketProductsPreferenceStateGet;

type SecondaryMarketProductsColumnStateActions = SecondaryMarketProductsColumnStateChanged;

type SecondaryMarketProductCodeSearchActions =
  | SecondaryMarketProductCodeTypeToggled
  | SecondaryMarketProductCodeSearchClear
  | SecondaryMarketProductsSearchedKeysSaved
  | SecondaryMarketProductsSearchedCodesFetching
  | SecondaryMarketProductsSearchedCodesFetchingFailed
  | SecondaryMarketProductsSearchedCodesSaveFailed
  | SecondaryMarketProductCodeSearchRequested
  | SecondaryMarketProductCodeSearchReceived
  | SecondaryMarketProductCodeSearchReloaded
  | SecondaryMarketProductCodeSearchFailed;

type SecondaryMarketProductsRemoveActions = SecondaryMarketProductsRemoveItem | SecondaryMarketProductsClearAll;

export type SecondaryMarketProductBlotterActions =
  | SecondaryMarketProductCodeSearchActions
  | SecondaryMarketProductsColumnsActions
  | SecondaryMarketProductsRemoveActions
  | SecondaryMarketProductsColumnStateActions
  | SecondaryMarketProductsPreferenceStateActions
  | SecondaryMarketProductRefreshActions
  | SecondaryMarketProductValueChangeActions
  | SecondaryMarketUnderlyingsAction
  | SecondaryMarketProductSearchFocused;

// ////////////////////////////////////

export type SecondaryMarketUnderlyingsAction =
  | SecondaryMarketUnderlyingsSearchRequested
  | SecondaryMarketUnderlyingsSearchReceived
  | SecondaryMarketUnderlyingsSearchFailed;

export function secondaryMarketProductCodeTypeChanged(newValue: ProductCodeType) {
  return { type: 'SECONDARY_MARKET_PRODUCT_CODE_TYPE_CHANGED' as const, newValue };
}

export type SecondaryMarketProductCodeTypeToggled = ReturnType<typeof secondaryMarketProductCodeTypeChanged>;

// ////////////////////////////////////

export function secondaryMarketProductRefreshRequested(keys: string[], dlpKeys: string[]) {
  return {
    type: 'SECONDARY_MARKET_PRODUCT_REFRESH_REQUESTED' as const,
    keys,
    dlpKeys,
  };
}
export type SecondaryMarketProductRefreshRequested = ReturnType<typeof secondaryMarketProductRefreshRequested>;

// ////////////////////////////////////

export function secondaryMarketProductRefreshReceived(
  keys: string[],
  dlpKeys: string[],
  items: SecondaryMarketProduct[],
) {
  return {
    type: 'SECONDARY_MARKET_PRODUCT_REFRESH_RECEIVED' as const,
    keys,
    dlpKeys,
    items,
  };
}

export type SecondaryMarketProductRefreshReceived = ReturnType<typeof secondaryMarketProductRefreshReceived>;

// ////////////////////////////////////

export function secondaryMarketProductRefreshFailed(keys: string[], dlpKeys: string[], error: unknown) {
  return {
    type: 'SECONDARY_MARKET_PRODUCT_REFRESH_FAILED' as const,
    keys,
    dlpKeys,
    error,
  };
}

export type SecondaryMarketProductRefreshFailed = ReturnType<typeof secondaryMarketProductRefreshFailed>;

// ////////////////////////////////////
export function secondaryMarketProductCodeSearchRequested(
  keys: string[],
  dlpKeys: string[],
  isAutomaticRequest: boolean,
) {
  return {
    type: 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_REQUESTED' as const,
    keys,
    dlpKeys,
    isAutomaticRequest,
  };
}
export type SecondaryMarketProductCodeSearchRequested = ReturnType<typeof secondaryMarketProductCodeSearchRequested>;

// ////////////////////////////////////

export function secondaryMarketProductCodeSearchClear(codes: readonly string[]) {
  return { type: 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_CLEAR' as const, codes };
}

export type SecondaryMarketProductCodeSearchClear = ReturnType<typeof secondaryMarketProductCodeSearchClear>;

// ////////////////////////////////////
export function secondaryMarketProductCodeSearchReceived(items: SecondaryMarketProduct[]) {
  return { type: 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_RECEIVED' as const, items };
}

export type SecondaryMarketProductCodeSearchReceived = ReturnType<typeof secondaryMarketProductCodeSearchReceived>;

// ////////////////////////////////////
export function secondaryMarketProductCodeSearchReloaded(items: SecondaryMarketProduct[]) {
  return { type: 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_RELOADED' as const, items };
}

export type SecondaryMarketProductCodeSearchReloaded = ReturnType<typeof secondaryMarketProductCodeSearchReloaded>;

// ////////////////////////////////////
export function secondaryMarketProductCodeSearchFailed(error?: string, timeout?: number) {
  return { type: 'SECONDARY_MARKET_PRODUCT_CODE_SEARCH_FAILED' as const, error, timeout };
}

export type SecondaryMarketProductCodeSearchFailed = ReturnType<typeof secondaryMarketProductCodeSearchFailed>;

// ////////////////////////////////////
export function secondaryMarketProductsColumnsFetching() {
  return { type: 'SECONDARY_MARKET_PRODUCT_COLUMNS_FETCHING' as const };
}

export type SecondaryMarketProductsColumnsFetching = ReturnType<typeof secondaryMarketProductsColumnsFetching>;

// ////////////////////////////////////
export function secondaryMarketProductsColumnsFetched(columns: IBlotterColumnDefinition<SecondaryMarketProduct>[]) {
  return { type: 'SECONDARY_MARKET_PRODUCT_COLUMNS_FETCHED' as const, columns };
}

export type SecondaryMarketProductsColumnsFetched = ReturnType<typeof secondaryMarketProductsColumnsFetched>;

// ////////////////////////////////////
export function secondaryMarketProductsColumnsFetchFailed(error: unknown) {
  return { type: 'SECONDARY_MARKET_PRODUCT_COLUMNS_FETCH_FAILED' as const, error };
}

export type SecondaryMarketProductsColumnsFetchFailed = ReturnType<typeof secondaryMarketProductsColumnsFetchFailed>;

// ////////////////////////////////////
// Persist searched codes
// ////////////////////////////////////

export function secondaryMarketProductsSearchedCodesFetching() {
  return { type: 'SECONDARY_MARKET_PRODUCT_SEARCHED_CODES_FETCHING' as const };
}

export type SecondaryMarketProductsSearchedCodesFetching = ReturnType<
  typeof secondaryMarketProductsSearchedCodesFetching
>;

export function secondaryMarketProductsSearchedCodesFetchingFailed(error?: string) {
  return { type: 'SECONDARY_MARKET_PRODUCT_SEARCHED_CODES_FETCHING_FAILED' as const, error };
}
export type SecondaryMarketProductsSearchedCodesFetchingFailed = ReturnType<
  typeof secondaryMarketProductsSearchedCodesFetchingFailed
>;

export function secondaryMarketProductsSearchedKeysSaved(keys: string[]) {
  return { type: 'SECONDARY_MARKET_PRODUCT_SEARCHED_CODES_SAVED' as const, keys };
}

export type SecondaryMarketProductsSearchedKeysSaved = ReturnType<typeof secondaryMarketProductsSearchedKeysSaved>;

export function secondaryMarketProductsSearchedCodesSaveFailed(message: string) {
  return { type: 'SECONDARY_MARKET_PRODUCT_SEARCHED_CODES_SAVE_FAILED' as const, message };
}

export type SecondaryMarketProductsSearchedCodesSaveFailed = ReturnType<
  typeof secondaryMarketProductsSearchedCodesSaveFailed
>;

// ////////////////////////////////////
// Clear data
// ////////////////////////////////////

export function secondaryMarketProductsRemoveItem(rowId: string) {
  return { type: 'SECONDARY_MARKET_PRODUCT_REMOVE_ITEM' as const, rowId };
}
export type SecondaryMarketProductsRemoveItem = ReturnType<typeof secondaryMarketProductsRemoveItem>;

export function secondaryMarketProductsClearAll() {
  return { type: 'SECONDARY_MARKET_PRODUCT_CLEAR_ALL' as const };
}

export type SecondaryMarketProductsClearAll = ReturnType<typeof secondaryMarketProductsClearAll>;

//////////////////////////////////////////////////
export interface SecondaryMarketProductsColumnStateChanged {
  type: 'SECONDARY_MARKET_PRODUCT_COLUMN_STATE_CHANGED';
  columnState: readonly ColumnPreferenceState[];
  columnGroupState?: readonly ColumnGroupPreferenceState[];
}
export function secondaryMarketProductsColumnStateChanged(
  columnState: readonly ColumnPreferenceState[],
  columnGroupState?: readonly ColumnGroupPreferenceState[],
): SecondaryMarketProductsColumnStateChanged {
  return { type: 'SECONDARY_MARKET_PRODUCT_COLUMN_STATE_CHANGED', columnState, columnGroupState };
}

//////////////////////////////////////////////////
export interface SecondaryMarketProductsPreferenceStatePersisted {
  type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_PERSISTED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function secondaryMarketProductsPreferenceStatePersisted(
  blotterPreferenceState: IBlotterPreferenceState,
): SecondaryMarketProductsPreferenceStatePersisted {
  return { type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_PERSISTED', blotterPreferenceState };
}

//////////////////////////////////////////////////
export interface SecondaryMarketProductsPreferenceStateLoaded {
  type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_LOADED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function secondaryMarketProductsPreferenceStateLoaded(
  blotterPreferenceState: IBlotterPreferenceState,
): SecondaryMarketProductsPreferenceStateLoaded {
  return { type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_LOADED', blotterPreferenceState };
}

//////////////////////////////////////////////////
export interface SecondaryMarketProductsPreferenceStateGet {
  type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_GET';
}
export function secondaryMarketProductsPreferenceStateGet(): SecondaryMarketProductsPreferenceStateGet {
  return { type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_GET' };
}

//////////////////////////////////////////////////
export function secondaryMarketProductsPreferenceStateLoadingFailed() {
  return {
    type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_LOADING_FAILED' as const,
  };
}
export type SecondaryMarketProductsPreferenceStateLoadingFailed = ReturnType<
  typeof secondaryMarketProductsPreferenceStateLoadingFailed
>;

////////////////////////////////////////////////
export interface SecondaryMarketProductsPreferenceStatePersistenceFailed {
  type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_PERSISTENCE_FAILED';
}
export function secondaryMarketProductsPreferenceStatePersistenceFailed(): SecondaryMarketProductsPreferenceStatePersistenceFailed {
  return { type: 'SECONDARY_MARKET_PRODUCT_PREFERENCE_STATE_PERSISTENCE_FAILED' };
}
////////////////////////////////////////////////

export interface ProductField {
  rowId: string;
  field: keyof SecondaryMarketProduct;
}
export type ProductChange = ProductField & {
  value: any;
  isAsync: boolean;
};

export type ProductChangeDone = ProductField & {
  hasBeenUpdated: boolean;
};

export function secondaryMarketProductsValueChanged(change: ProductChange) {
  return { type: 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED' as const, change };
}

export type SecondaryMarketProductsValueChanged = ReturnType<typeof secondaryMarketProductsValueChanged>;

////////////////////////////////////////////////

export function secondaryMarketProductsValueChangedRequest(change: ProductChange) {
  return { type: 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_REQUEST' as const, change };
}
export type SecondaryMarketProductsValueChangedRequest = ReturnType<typeof secondaryMarketProductsValueChangedRequest>;

////////////////////////////////////////////////
export function secondaryMarketProductsSalesMessageChangedRequest(change: ProductChange) {
  return { type: 'SECONDARY_MARKET_PRODUCTS_SALES_MESSAGE_CHANGED_REQUEST' as const, change };
}
export type SecondaryMarketProductsSalesMessageChangedRequest = ReturnType<
  typeof secondaryMarketProductsSalesMessageChangedRequest
>;

////////////////////////////////////////////////
export function secondaryMarketProductsCustodianOrClientChangedRequest(change: ProductChange) {
  return { type: 'SECONDARY_MARKET_PRODUCTS_CUSTODIAN_OR_CLIENT_CHANGED_REQUEST' as const, change };
}
export type SecondaryMarketProductsCustodianOrClientChangedRequest = ReturnType<
  typeof secondaryMarketProductsCustodianOrClientChangedRequest
>;

////////////////////////////////////////////////

export function secondaryMarketProductsValueChangedFailed(fields: ProductField[], errors?: string) {
  return { type: 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_FAILED' as const, fields, errors };
}

export type SecondaryMarketProductsValueChangedFailed = ReturnType<typeof secondaryMarketProductsValueChangedFailed>;

export function secondaryMarketProductsValueChangedSetEditableInfo(
  rowId: string,
  field: keyof SecondaryMarketProduct,
  info: EditableFieldInfo,
) {
  return { type: 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_SET_EDITABLE_INFO' as const, rowId, field, info };
}

export type SecondaryMarketProductsValueChangedSetEditableInfo = ReturnType<
  typeof secondaryMarketProductsValueChangedSetEditableInfo
>;

////////////////////////////////////////////////
export function secondaryMarketProductsValueChangedDone(
  fields: ProductChangeDone[],
  errors?: string,
  warnings?: string,
) {
  return { type: 'SECONDARY_MARKET_PRODUCTS_VALUE_CHANGED_DONE' as const, fields, errors, warnings };
}

export type SecondaryMarketProductsValueChangedDone = ReturnType<typeof secondaryMarketProductsValueChangedDone>;

////////////////////////////////////////////////
export function secondaryMarketUnderlyingsSearchRequested(keys: string[]) {
  return {
    type: 'SECONDARY_MARKET_UNDERLYINGS_SEARCH_REQUESTED' as const,
    keys,
  };
}

export type SecondaryMarketUnderlyingsSearchRequested = ReturnType<typeof secondaryMarketUnderlyingsSearchRequested>;

////////////////////////////////////////////////
export function secondaryMarketUnderlyingsSearchReceived(underlyings: Underlying[]) {
  return {
    type: 'SECONDARY_MARKET_UNDERLYINGS_SEARCH_RECEIVED' as const,
    underlyings,
  };
}

export type SecondaryMarketUnderlyingsSearchReceived = ReturnType<typeof secondaryMarketUnderlyingsSearchReceived>;

////////////////////////////////////////////////
export function secondaryMarketUnderlyingsSearchFailed(keys: string[], error?: string) {
  return {
    type: 'SECONDARY_MARKET_UNDERLYINGS_SEARCH_FAILED' as const,
    keys,
    error,
  };
}

export type SecondaryMarketUnderlyingsSearchFailed = ReturnType<typeof secondaryMarketUnderlyingsSearchFailed>;

////////////////////////////////////////////////
export function secondaryMarketProductSearchFocused(id?: string) {
  return {
    type: 'SECONDARY_MARKET_PRODUCT_SEARCH_FOCUSED' as const,
    id,
  };
}

export type SecondaryMarketProductSearchFocused = ReturnType<typeof secondaryMarketProductSearchFocused>;
